import React, { useEffect, useState } from "react";
import { Container, Box, Typography, CircularProgress } from "@mui/material";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebaseConfig";
import TopBar from "../components/portal/TopBar";
import PageTabs from "../components/portal/PageTabs";
import FolderSection from "../components/portal/FolderSection";
import TileGrid from "../components/portal/TileGrid";
import {
  getCurrentUser,
  getPages,
  getFolders,
  getPageItems,
  filterVisibleItems,
  groupItemsByFolder,
  updateLastLogin,
} from "../services/portalService";

export default function PortalPage() {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [portalUser, setPortalUser] = useState(null);
  const [pages, setPages] = useState([]);
  const [activePageId, setActivePageId] = useState(null);
  const [folders, setFolders] = useState([]);
  const [groupedFolders, setGroupedFolders] = useState([]);
  const [ungroupedItems, setUngroupedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setFirebaseUser(user);
        const userData = await getCurrentUser(user.uid);
        setPortalUser(userData);

        if (userData) {
          const userRoles = userData.roles || [];
          setIsAdmin(userRoles.includes("admin"));
          await updateLastLogin(user.uid);
        }

        const pagesData = await getPages();
        setPages(pagesData);

        if (pagesData.length > 0) {
          setActivePageId(pagesData[0].id);
        }
      } else {
        setFirebaseUser(null);
        setPortalUser(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    async function loadPageContent() {
      if (!activePageId || !portalUser) return;

      setLoading(true);

      const [foldersData, itemsData] = await Promise.all([
        getFolders(activePageId),
        getPageItems(activePageId),
      ]);

      const userRoles = portalUser.roles || [];
      const department = portalUser.department || "";

      const visibleItems = filterVisibleItems(itemsData, userRoles, department);
      const grouped = groupItemsByFolder(visibleItems, foldersData);

      setFolders(foldersData);
      setGroupedFolders(grouped.folders);
      setUngroupedItems(grouped.ungrouped);
      setLoading(false);
    }

    loadPageContent();
  }, [activePageId, portalUser]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!firebaseUser) {
    return (
      <Container>
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography variant="h5">Please log in to access the portal</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5" }}>
      <TopBar user={firebaseUser} isAdmin={isAdmin} />

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {pages.length > 0 && (
          <PageTabs
            pages={pages}
            activePageId={activePageId}
            onPageChange={setActivePageId}
          />
        )}

        {groupedFolders.map((folder) => (
          <FolderSection key={folder.id} folder={folder} items={folder.items} />
        ))}

        {ungroupedItems.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Other Tools
            </Typography>
            <TileGrid items={ungroupedItems} />
          </Box>
        )}

        {groupedFolders.length === 0 && ungroupedItems.length === 0 && (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              No tools available for this page
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
}
